/**
 * LinePriceReasonPrompt
 *
 * The "little thing" the owner asked for (2026-10-29, review amn8ne): after a
 * CSR saves a draft line price that differs from what the PO said, this row
 * appears under the line and offers an OPTIONAL one-line note on why. The
 * price is already saved; nothing here blocks keying or navigation. Save
 * sends the note (PATCH price_change_reason → its own audit row); Skip just
 * closes it. Enter saves, Esc skips, an empty Save is a Skip.
 *
 * Never a confirm, never a warning: the owner ruled a hand-keyed price is the
 * CSR's choice (2026-10-05). This only gives the choice a voice in the record.
 */
import { useEffect, useRef, useState } from 'react';
import { PRICE_REASON_MAX_LEN } from './linePriceReason';
import styles from './LinePriceReasonPrompt.module.css';

export interface LinePriceReasonPromptProps {
  lineItemId: string;
  itemCode: string;
  /** What the customer wrote on the PO. */
  poPrice: string;
  /** The price the CSR just saved. */
  newPrice: string;
  formatPrice: (val: string | number | null | undefined) => string;
  saving?: boolean;
  onSave: (reason: string) => void;
  onSkip: () => void;
}

export default function LinePriceReasonPrompt({
  lineItemId,
  itemCode,
  poPrice,
  newPrice,
  formatPrice,
  saving = false,
  onSave,
  onSkip,
}: LinePriceReasonPromptProps) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Take focus so Enter/Esc work straight away, but only once per prompt —
  // the CSR may still be tabbing through the cells of the next line.
  useEffect(() => {
    inputRef.current?.focus();
  }, [lineItemId]);

  const direction = parseFloat(newPrice) > parseFloat(poPrice) ? 'above' : 'below';

  const submit = () => {
    const text = draft.trim();
    if (!text) {
      onSkip();
      return;
    }
    onSave(text.slice(0, PRICE_REASON_MAX_LEN));
  };

  return (
    <div
      className={styles.prompt}
      data-testid={`price-reason-prompt-${lineItemId}`}
      role="group"
      aria-label={`Why is ${itemCode} priced differently from the PO?`}
    >
      <div className={styles.text}>
        <strong>{formatPrice(newPrice)}</strong> is {direction} the PO price of{' '}
        <strong>{formatPrice(poPrice)}</strong> for {itemCode}. Want to note why? <span className={styles.optional}>(optional)</span>
      </div>
      <div className={styles.controls}>
        <input
          ref={inputRef}
          className={styles.input}
          type="text"
          value={draft}
          maxLength={PRICE_REASON_MAX_LEN}
          placeholder="e.g. rep re-issued the price list 10/19"
          aria-label="Reason for the price change"
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onSkip();
            }
          }}
          data-testid={`price-reason-input-${lineItemId}`}
        />
        <button
          type="button"
          className={styles.save}
          disabled={saving}
          onClick={submit}
          data-action="order_line_price_reason"
          data-target-item-id={lineItemId}
          data-outcome="request"
          data-testid={`price-reason-save-${lineItemId}`}
        >
          {saving ? 'Saving…' : 'Save note'}
        </button>
        <button
          type="button"
          className={styles.skip}
          disabled={saving}
          onClick={onSkip}
          data-action="order_line_price_reason_skip"
          data-target-item-id={lineItemId}
          data-outcome="none"
          data-testid={`price-reason-skip-${lineItemId}`}
        >
          Skip
        </button>
      </div>
    </div>
  );
}
